import { useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useLazyQuery, useQuery, useMutation } from '@apollo/client'
import { Toast } from 'lib/ui/components'
import { GetRmaOrderView, getAttributes, CreateRmaOrder } from 'services/Rma'

interface RmaOrderAddress {
  city?: string
  country?: string
  firstname?: string
  lastname?: string
  postcode?: string
  region?: string
  street?: string
  telephone?: string
}

interface RmaOrderItem {
  item_id: number
  name: string
  price: number
  qty_ordered: number
  sku: string
  subtotal: number
  is_returnable: string
}

export interface RmaOrder {
  billing_address?: RmaOrderAddress
  created_at?: string
  customer_name?: string
  discount: number
  grand_total: number
  increment_id?: string
  items?: RmaOrderItem[]
  payment_method?: string
  shipping_address?: RmaOrderAddress
  shipping_amount: number
  shipping_method?: string
  subtotal: number
  status: string
}

interface RmaOrderItemInput {
  condition: number
  order_item_id: number
  qty_requested: number
  reason: number
  reason_other?: string
  resolution: number
}

export interface OrderInfo {
  lastname: string
  orderId: string
  search: string
  type: boolean
}

export interface CreateReturn extends OrderInfo{
  customerCustomEmail?: string
  rmaComment?: string
  items: RmaOrderItemInput[]
}

export const useOrderReturns = () => {
  const history = useHistory()
  const [orderInfo, setOrderInfo] = useState<OrderInfo | undefined>()
  const { loading: attributesLoading, data: formAttributesData } = useQuery(getAttributes)
  const [getRmaOrderData, { data, loading: orderDataLoading }] = useLazyQuery(GetRmaOrderView, {
    onCompleted: () => {
      if (!data?.GetRmaOrder?.result) {
        Toast.toastFailure({ messageId: 'returns.invalid.data' })
      }
      history.push('/sales/guest/view')
    },
    onError: () => Toast.toastFailure({ messageId: 'app.whoops' })
  })
  const [createReturnOrder, { data: mutationResult, loading: creatingOrder }] = useMutation(CreateRmaOrder)
  const rmaOrderData: RmaOrder = data?.GetRmaOrder?.order
  const orderResult = mutationResult?.CreateRmaOrder
  const formAttributes = formAttributesData?.GetRMAFormAttributes?.attributes
  const getOrderData = (orderInfo: OrderInfo) => {
    setOrderInfo(orderInfo)
    getRmaOrderData({
      variables: orderInfo
    })
  }

  const createReturn = (values: CreateReturn) => {
    createReturnOrder({
      variables: values
    })
      .then(({ data }) => {
        const message = data.CreateRmaOrder?.message
        const creatingSuccess = data.CreateRmaOrder?.result === true
        if (creatingSuccess) {
          Toast.toastSuccess({ message })
        } else {
          Toast.toastFailure({ message })
        }
      })
      .catch(() => Toast.toastFailure({ messageId: 'app.whoops' }))
  }

  return {
    rmaOrderData,
    getOrderData,
    orderDataLoading,
    formAttributes,
    attributesLoading,
    createReturn,
    orderInfo,
    orderResult,
    creatingOrder
  }
}
